import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  getDocs,
  setDoc,
  query,
  where,
} from "firebase/firestore";
import { uploadImage, deleteImage } from "./supabase";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const generateTrackingCode = () => {
  return `TS-${Math.floor(1000 + Math.random() * 9000)}`;
};

const uploadAllImages = async (lanterns) => {
  const updatedLanterns = [];

  for (const lantern of lanterns) {
    if (
      lantern.imageURL &&
      typeof lantern.imageURL === "object" &&
      lantern.imageURL.type === "file"
    ) {
      console.log("Uploading image for day", lantern.day);
      const { url, error } = await uploadImage(
        lantern.imageURL.file,
        "ramadan-gifts",
      );

      if (error) {
        console.error("Error uploading image:", error);
        updatedLanterns.push({
          ...lantern,
          imageURL: "",
        });
      } else {
        updatedLanterns.push({
          ...lantern,
          imageURL: url, 
        });
      }
    } else {
      updatedLanterns.push({
        ...lantern,
        imageURL: typeof lantern.imageURL === "string" ? lantern.imageURL : "",
      });
    }
  }

  return updatedLanterns;
};

const deleteAllGiftImages = async (gift) => {
  if (!gift.lanterns) return;

  for (const lantern of gift.lanterns) {
    if (lantern.imageURL && lantern.imageURL.includes("supabase")) {
      console.log("Deleting image:", lantern.imageURL);
      await deleteImage(lantern.imageURL);
    }
  }
};

export const adminStorage = {
  getAdminPassword: async () => {
    try {
      const docSnap = await getDoc(doc(db, "admin", "password"));
      if (docSnap.exists()) {
        return docSnap.data().password || "admin123";
      }
      return "admin123";
    } catch (error) {
      console.error("Error getting admin password:", error);
      return "admin123";
    }
  },

  updateAdminPassword: async (newPassword) => {
    try {
      await updateDoc(doc(db, "admin", "password"), { password: newPassword });
    } catch (error) {
      try {
        await setDoc(doc(db, "admin", "password"), { password: newPassword });
      } catch (setError) {
        console.error("Error setting admin password:", setError);
      }
    }
  },

  getSettings: async () => {
    try {
      const docSnap = await getDoc(doc(db, "admin", "settings"));
      if (docSnap.exists()) {
        return docSnap.data();
      }
      return {
        musicUrl:
          "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
        musicEnabled: true,
      };
    } catch (error) {
      console.error("Error getting settings:", error);
      return {
        musicUrl:
          "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
        musicEnabled: true,
      };
    }
  },

  updateSettings: async (settings) => {
    try {
      await updateDoc(doc(db, "admin", "settings"), settings);
    } catch (error) {
      try {
        await setDoc(doc(db, "admin", "settings"), settings);
      } catch (setError) {
        console.error("Error setting settings:", setError);
      }
    }
  },
};

export const firebaseStorage = {
  trackGiftByCode: async (trackingCode) => {
    try {
      const q = query(
        collection(db, "gifts"),
        where("trackingCode", "==", trackingCode.toUpperCase()),
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error("Error tracking gift by code:", error);
      throw error;
    }
  },

  saveGift: async (gift) => {
    try {
      const isFree = gift.planType === "free";
      console.log("Processing gift images...");
      const processedLanterns = await uploadAllImages(gift.lanterns || []);

      const sanitizedGift = {
        ...gift,
        lanterns: processedLanterns,
        trackingCode: isFree ? null : generateTrackingCode(),
        paymentProof: gift.paymentProof || "",
        paymentStatus: isFree ? "approved" : "pending",
      };

      const docRef = await addDoc(collection(db, "gifts"), sanitizedGift);

      return {
        id: docRef.id,
        trackingCode: sanitizedGift.trackingCode,
        isFree: isFree,
      };
    } catch (error) {
      console.error("Error saving gift:", error);
      throw error;
    }
  },

  getGift: async (id) => {
    try {
      const docSnap = await getDoc(doc(db, "gifts", id));
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error("Error getting gift:", error);
      return null;
    }
  },

  updateGift: async (id, updates) => {
    try {
      await updateDoc(doc(db, "gifts", id), updates);
    } catch (error) {
      console.error("Error updating gift:", error);
    }
  },

  deleteGift: async (id) => {
    try {
      const gift = await firebaseStorage.getGift(id);
      if (gift) {
        await deleteAllGiftImages(gift);
      }

      await deleteDoc(doc(db, "gifts", id));
    } catch (error) {
      console.error("Error deleting gift:", error);
    }
  },

  getAllGifts: async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "gifts"));
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting all gifts:", error);
      return [];
    }
  },

  getStats: async () => {
    const gifts = await firebaseStorage.getAllGifts();
    const freeUsers = gifts.filter((g) => g.planType === "free").length;
    const paidUsers = gifts.filter((g) => g.planType !== "free").length;
    const basicPlans = gifts.filter((g) => g.planType === "basic").length;
    const premiumPlans = gifts.filter((g) => g.planType === "premium").length;
    const pendingPayments = gifts.filter(
      (g) => g.paymentStatus === "pending",
    ).length;
    return { freeUsers, paidUsers, basicPlans, premiumPlans, pendingPayments };
  },
};
